import React, { useEffect } from "react";
import { useFragment } from "react-relay";
import { useHistory } from "react-router-dom";

import { faBullseyeArrow, faChartBar, faFileLines, faThumbsUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCircleUp } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { differenceInMilliseconds } from "date-fns";
import { graphql } from "relay-runtime";

import { showFeatureModal } from "~/billing";
import type { FeatureValues } from "~/billing/utils";
import { Menu } from "~/ui/menu";

import { ReactComponent as MoreIconActive } from "./icons/more-filled.svg";
import { ReactComponent as MoreIcon } from "./icons/more-outline.svg";

import type { MoreNavItem_viewer$key } from "~/__generated__/MoreNavItem_viewer.graphql";

import styles from "./MoreNavItem.module.css";
import navStyles from "./nav.module.css";

export const MORE_MENU_PANEL_KEYS = ["feedback", "templates", "objectives", "analytics"] as const;
export type MoreMenuPanelKey = (typeof MORE_MENU_PANEL_KEYS)[number];

const viewerFragment = graphql`
	fragment MoreNavItem_viewer on Viewer {
		hasAdminAccess
		account {
			billingAccount {
				hasAccessToFeedback: hasAccessToFeature(feature: feedback)
				hasAccessToObjectives: hasAccessToFeature(feature: objectives)
				hasAccessToAnalytics: hasAccessToFeature(feature: analytics)
			}
		}
	}
`;

interface MenuOption {
	icon: IconDefinition;
	panelKey: MoreMenuPanelKey;
	label: string;
	description: string;
	paywallFeatureKey?: FeatureValues;
	hideIfNoAccess?: boolean;
	defaultRoute?: string;
}

const MENU_OPTIONS: MenuOption[] = [
	{
		icon: faBullseyeArrow,
		panelKey: "objectives",
		label: "Objectives",
		description: "Create and track progress towards goals",
		paywallFeatureKey: "objectives",
		hideIfNoAccess: true,
		defaultRoute: "/objectives/",
	},
	{
		icon: faThumbsUp,
		panelKey: "feedback",
		label: "Feedback",
		description: "Request and give feedback on meetings and peers",
		paywallFeatureKey: "feedback",
		hideIfNoAccess: true,
	},
	{
		icon: faFileLines,
		panelKey: "templates",
		label: "Templates",
		description: "Create and manage custom templates",
		defaultRoute: "/templates-section/notes/",
	},
	{
		icon: faChartBar,
		panelKey: "analytics",
		label: "Analytics",
		description: "Key metrics for your meetings",
		defaultRoute: "/analytics/",
		paywallFeatureKey: "analytics",
	},
];

interface MoreMenuItemProps extends MenuOption {
	hasAccessToFeature: boolean;
	hasAdminAccess: boolean;
	dispatchPanel: (key: MoreMenuPanelKey) => void;
	onPaywallModalOpen?: () => void;
}

function MoreMenuItem({
	label,
	description,
	icon,
	dispatchPanel,
	onPaywallModalOpen,
	panelKey,
	hasAccessToFeature,
	hasAdminAccess,
	paywallFeatureKey,
	defaultRoute,
}: MoreMenuItemProps) {
	const history = useHistory();
	const onSelect = React.useCallback(() => {
		// Special case for analytics, we want users to land on the Analytics page instead of seeing the paywall modal
		if (hasAccessToFeature || paywallFeatureKey === "analytics") {
			// Don't show the AnalyticsPanel to non-admin users
			if (paywallFeatureKey !== "analytics" || hasAdminAccess) dispatchPanel(panelKey);

			if (defaultRoute) history.push(defaultRoute);
		} else if (paywallFeatureKey) {
			if (onPaywallModalOpen) onPaywallModalOpen();
			showFeatureModal(paywallFeatureKey);
		}
	}, [
		defaultRoute,
		history,
		hasAccessToFeature,
		hasAdminAccess,
		onPaywallModalOpen,
		dispatchPanel,
		panelKey,
		paywallFeatureKey,
	]);

	return (
		<Menu.Item
			icon={hasAccessToFeature ? icon : faCircleUp}
			iconProps={{
				style: { fontSize: "1rem" },
				color: hasAccessToFeature ? "var(--secondaryTextColor)" : "var(--billingColor)",
			}}
			className={styles.moreMenuItem}
			onSelect={onSelect}
			data-test-id={`nav-button--${panelKey}`}
		>
			<div className={styles.moreMenuItemContent}>
				<span>{label}</span>
				<span>
					<small>{description}</small>
				</span>
			</div>
		</Menu.Item>
	);
}

interface MoreNavItemProps extends Pick<MoreMenuItemProps, "dispatchPanel" | "onPaywallModalOpen"> {
	viewer: MoreNavItem_viewer$key;
	isActive: boolean;
}

export const isPaywallEnabled = (paywallsEnabled: Partial<Record<FeatureValues, boolean>>, key: FeatureValues) => {
	return key in paywallsEnabled ? (paywallsEnabled[key] as boolean) : false;
};

export default function MoreNavItem({
	viewer: viewerKey,
	isActive,
	dispatchPanel,
	onPaywallModalOpen,
}: MoreNavItemProps) {
	const {
		hasAdminAccess,
		account: {
			billingAccount: { hasAccessToAnalytics, hasAccessToFeedback, hasAccessToObjectives },
		},
	} = useFragment(viewerFragment, viewerKey);
	const paywallsEnabled: Partial<Record<FeatureValues, boolean>> = {
		feedback: hasAccessToFeedback,
		objectives: hasAccessToObjectives,
		analytics: hasAccessToAnalytics,
	};

	const [openedByHover, setOpenedByHover] = React.useState(false);
	const [openedByClick, setOpenedByClick] = React.useState(false);
	const [lastOpenedAt, setLastOpenedAt] = React.useState<Date>(new Date());
	const onOpenChange = React.useCallback(
		(open: boolean) => {
			// This stops the user from closing the menu as soon as it opens by clicking it as soon as it appears
			if (!open && differenceInMilliseconds(new Date(), lastOpenedAt) < 500) {
				setOpenedByClick(true);
				return;
			}

			setOpenedByHover(open);
			setOpenedByClick(open);
		},
		[lastOpenedAt],
	);

	const hoverTimeout = React.useRef<number>();
	const clearHoverTimeout = React.useCallback(() => window.clearTimeout(hoverTimeout.current), [hoverTimeout]);

	const startCloseMenuTimeout = React.useCallback(() => {
		clearHoverTimeout();
		hoverTimeout.current = window.setTimeout(() => setOpenedByHover(false), 250);
	}, [clearHoverTimeout]);

	const startOpenMenuTimeout = React.useCallback(() => {
		clearHoverTimeout();
		hoverTimeout.current = window.setTimeout(() => setOpenedByHover(true), 250);
	}, [clearHoverTimeout]);

	useEffect(() => {
		return () => {
			// Clear timeout for good measure
			clearHoverTimeout();
		};
	}, [clearHoverTimeout]);

	useEffect(() => {
		if (openedByClick || openedByHover) setLastOpenedAt(new Date());
	}, [openedByClick, openedByHover]);

	return (
		<Menu open={openedByClick || openedByHover} onOpenChange={onOpenChange}>
			<Menu.Trigger>
				<button
					data-test-id="nav-button--more"
					className={navStyles.navButton}
					data-active={isActive}
					onMouseEnter={startOpenMenuTimeout}
					onMouseLeave={startCloseMenuTimeout}
				>
					<div className={navStyles.navButtonIconWrapper} aria-hidden>
						{isActive ? <MoreIconActive /> : <MoreIcon />}
					</div>
					<span className={navStyles.navButtonLabel}>More</span>
				</button>
			</Menu.Trigger>
			<Menu.Content
				className={styles.moreMenuContent}
				side="right"
				align="start"
				onMouseEnter={clearHoverTimeout}
				onMouseLeave={startCloseMenuTimeout}
			>
				{MENU_OPTIONS.map(item => {
					const { paywallFeatureKey, panelKey } = item;
					const hasAccessToFeature = paywallFeatureKey ? isPaywallEnabled(paywallsEnabled, paywallFeatureKey) : true;

					if (item.hideIfNoAccess && !hasAccessToFeature) return null;

					return (
						<MoreMenuItem
							{...item}
							key={panelKey}
							hasAdminAccess={hasAdminAccess}
							hasAccessToFeature={hasAccessToFeature}
							dispatchPanel={dispatchPanel}
							onPaywallModalOpen={onPaywallModalOpen}
						/>
					);
				})}
			</Menu.Content>
		</Menu>
	);
}
